import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type MutableRefObject,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCommentsSyncController } from '@/pages/docs/hooks/useCommentsSyncController'
import { applyBlockToEditor } from '@/pages/screenata/policies/apply-block-to-editor'
import type {
  GenerationItem, // PolicyItem,
  PolicySubmissionMeta,
  TriageClaim,
  PolicyClaimHighlight,
} from '@/pages/screenata/policies/types'
import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Switch } from '@deepguide-ai/ui/switch'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@deepguide-ai/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { PluginKey } from '@tiptap/pm/state'
import type { Editor } from '@tiptap/react'
import { useSetAtom } from 'jotai'
import {
  AlertTriangle,
  Activity,
  FileText,
  Loader2,
  Shield,
  History,
  RefreshCw,
  MessageSquare,
  Sparkles,
  Upload,
  SlidersHorizontal,
  Highlighter,
  GitCompareArrows,
  Info,
} from 'lucide-react'
import { toast } from 'sonner'
import { editorBlockOutlineAtom } from '@/stores/editorBlockOutlineAtom'
import {
  chatProgrammaticMessageAtom,
  screenataAssistantOpenAtom,
  screenataPageContextAtom,
} from '@/stores/screenataPageContextStore'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  extractBlockOutline,
  formatBlockOutline,
} from '@/components/chat/lib/apply-blocks'
import { CommentPanel } from '@/components/comments'
import {
  PolicyContentViewer,
  hasRenderablePolicyContent,
} from '@/components/screenata/PolicyContentViewer'
import {
  buildPolicyClaimBlockMaps,
  registerPolicyClaimBadgeDecorations,
  scrollToPolicyClaimBlock,
  syncPolicyClaimBlockDecorations,
  syncPolicyClaimBlockTargets,
} from '@/components/screenata/policy-claim-blocks'
import {
  PolicyDetailDocumentSkeleton,
  usePolicyDraftClaimMutations,
} from '@/components/screenata/policy-detail'
import { AssumptionCard } from '@/components/screenata/policy-detail/AssumptionCard'
import { EditorErrorBoundary } from '@/components/screenata/policy-detail/EditorErrorBoundary'
import { PolicyQuestionnaireDialog } from '@/components/screenata/policy-detail/PolicyQuestionnaireDialog'
import { PolicyUploadPanel } from '@/components/screenata/PolicyUploadPanel'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { PolicyCommitmentsPanel } from '@/components/screenata/policy-detail/PolicyCommitmentsPanel'
import {
  PolicyHealthPanel,
} from '@/components/screenata/policy-detail/PolicyHealthPanel'
import { PolicyPropertiesPanel } from '@/components/screenata/policy-detail/PolicyPropertiesPanel'
import type { PolicyItem } from '@/components/screenata/policy-detail/types'
import {
  TiptapDiffView,
  type TiptapDiffViewMode,
} from '@/components/tiptap/TiptapDiffView'

export default function PolicyDetailPanel({
  item,
  policyData: _policyData,
  workspaceId,
  auditCycleId,
  programId,
  mode,
  isEditing,
  editorRef,
  showClaims,
  onShowClaimsChange,
  showHighlights = false,
  onShowHighlightsChange,
  claims: claimsProp,
  viewingVersion,
  versionContent,
  versionLoading,
  versionError = null,
  versions = [],
  compareVersion = null,
  onCompareVersionChange = () => {},
  compareDialogOpenRequest = 0,
  compareVersionContent = null,
  compareVersionLoading = false,
  compareVersionError = null,
  submittedVersion = null,
  submittedVersionContent = null,
  submittedVersionLoading = false,
  submittedVersionError = null,
  submissionMeta,
  preferredSidebarTab,
  focusedClaimId,
  focusedClaimBlockId,
  onSidebarTabChange,
  onOpenCommentThreadCountChange,
  onRevert,
  revertPending,
  onEditorUpdate,
  onEditorCreate,
  onLiveContentLoaded,
  onPolicyDocChange,
  mainBanner,
  submissionMetaBar,
  embedInOverlay = false,
  disableApplyBlockListener = false,
  enableComments = true,
  hideCommentComposer = false,
}: {
  item: GenerationItem
  policyData?: PolicyItem | null
  workspaceId: string
  auditCycleId?: string
  programId: string
  mode: 'manage' | 'readonly'
  isEditing: boolean
  editorRef: MutableRefObject<any>
  showClaims?: boolean
  onShowClaimsChange?: (show: boolean) => void
  showHighlights?: boolean
  onShowHighlightsChange?: (show: boolean) => void
  claims?: TriageClaim[]
  viewingVersion: number | null
  versionContent: { tiptapJson: any; content: string } | null
  versionLoading: boolean
  versionError?: string | null
  versions?: {
    version: number
    approvalSignature: unknown
    createdAt?: string
    changeNote?: string | null
  }[]
  compareVersion?: number | null
  onCompareVersionChange?: (version: number | null) => void
  compareDialogOpenRequest?: number
  compareVersionContent?: { tiptapJson: any; content: string } | null
  compareVersionLoading?: boolean
  compareVersionError?: string | null
  submittedVersion?: number | null
  submittedVersionContent?: { tiptapJson: any; content: string } | null
  submittedVersionLoading?: boolean
  submittedVersionError?: string | null
  submissionMeta?: PolicySubmissionMeta
  preferredSidebarTab?: 'properties' | 'claims' | 'comments' | 'assumptions'
  focusedClaimId?: string
  focusedClaimBlockId?: string
  onSidebarTabChange?: (
    tab: 'properties' | 'claims' | 'comments' | 'assumptions'
  ) => void
  onOpenCommentThreadCountChange?: (count: number) => void
  onRevert: () => void
  revertPending: boolean
  onEditorUpdate?: ({ editor }: { editor: any }) => void
  onEditorCreate?: (toolbar: {
    editor: Editor
    onOpenComments?: () => void
    onAskVera: (payload: { quoteText?: string; docBlockId?: string }) => void
    onAddClaimFromSelection?: (payload: {
      docBlockId: string
      quoteText: string
      offset: number
    }) => void
  }) => void
  onLiveContentLoaded?: (policyDocumentId: string) => void
  // Callback when policy doc id is detected (for parent to track)
  onPolicyDocChange?: (policyDocumentId: string) => void
  /** Banner rendered at the top of the main editor column (not above the
   * sidebar). Used for the "Vera drafted this policy" review notice so the
   * strip spans only the document area, leaving the Properties/Claims panel
   * flush to the top. */
  mainBanner?: React.ReactNode
  /** Approval metadata rendered within the document-width status bar. */
  submissionMetaBar?: React.ReactNode
  /** Main-panel overlay: avoid viewport-based min-heights that clip scrolling. */
  embedInOverlay?: boolean
  /**
   * Skip this panel's own `screenata:apply-block` listener. Set by hosts that
   * already register `usePolicyEditorEvents` (which owns approved→draft
   * branching), to avoid a redundant listener mutating the approved version.
   */
  disableApplyBlockListener?: boolean
  enableComments?: boolean
  hideCommentComposer?: boolean
}) {
  const navigate = useNavigate()
  const isReadonlyMode = mode === 'readonly'
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  // Auditor policy metadata also covers approved policies without a submission.
  const canRenderClaimDecorations = !isReadonlyMode || Boolean(submissionMeta)
  const canTriageClaims = !isReadonlyMode
  const queryClient = useQueryClient()
  const setBlockOutline = useSetAtom(editorBlockOutlineAtom)
  const setChatMessage = useSetAtom(chatProgrammaticMessageAtom)
  const setAssistantOpen = useSetAtom(screenataAssistantOpenAtom)
  const setPageContext = useSetAtom(screenataPageContextAtom)
  const [showSidebar, setShowSidebar] = useState(true)
  const [sidebarTab, setSidebarTab] = useState<
    'properties' | 'claims' | 'comments' | 'assumptions' | 'health'
  >('properties')
  const [missingCoverageOnly, setMissingCoverageOnly] = useState(false)
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null)
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null)
  const [activeCommentSource, setActiveCommentSource] = useState<
    'panel' | 'editor'
  >('panel')
  const [editorMountCount, setEditorMountCount] = useState(0)
  const [claimToDelete, setClaimToDelete] = useState<any>(null)
  const [compareDialogManuallyOpen, setCompareDialogManuallyOpen] =
    useState(false)
  const [compareViewMode, setCompareViewMode] =
    useState<TiptapDiffViewMode>('side-by-side')
  const lastCompareDialogOpenRequestRef = useRef(compareDialogOpenRequest)

  useEffect(
    () => () => {
      setBlockOutline(null)
    },
    [setBlockOutline]
  )

  // Set page context for chat awareness of current policy being viewed
  useEffect(() => {
    const policyStatus = isEditing ? 'draft' : 'approved'
    const data: Record<string, unknown> = {
      policySlug: _policyData?.slug || item.slug,
      status: policyStatus,
    }
    if (item.currentVersion != null) {
      data.draftVersion = item.currentVersion
    }
    if (item.approvedVersion != null) {
      data.approvedVersion = item.approvedVersion
    }
    data.viewingVersion =
      viewingVersion ?? (isEditing ? item.currentVersion : item.approvedVersion)

    // Extract blockId from current editor selection to pass context to agent
    if (editorRef && editorRef.state && editorRef.state.selection) {
      const { from } = editorRef.state.selection
      let blockId: string | undefined
      editorRef.state.doc.nodesBetween(from, from, (node) => {
        if (node.attrs?.id && !blockId) {
          blockId = node.attrs.id
        }
      })
      if (blockId) {
        data.blockId = blockId
      }
    }

    setPageContext({
      page: 'policy-detail',
      summary: `Viewing ${_policyData?.slug || item.slug} (${policyStatus})`,
      data,
    })
    return () => setPageContext(null) // Clear on unmount
  }, [
    _policyData?.slug,
    item.slug,
    isEditing,
    viewingVersion,
    item.currentVersion,
    item.approvedVersion,
    editorRef,
    setPageContext,
  ])

  // Empty-policy entry points: bring your own document, or answer this
  // policy's questions then generate from the template.
  const [uploadOpen, setUploadOpen] = useState(false)
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false)

  const openPolicyChat = useCallback(() => {
    void navigate({
      to: '/workspaces/$workspaceId/policies/$policySlug',
      params: { workspaceId, policySlug: item.slug },
      search: { regenerate: true },
    })
  }, [item.slug, navigate, workspaceId])

  const isBackgroundDrafting =
    item.generationState === 'queued' || item.generationState === 'generating'
  // Show the draft even while the iteration loop is still revising —
  // the writer has already saved content; keeping the full-page
  // placeholder up through the review/revise passes confuses users
  // into thinking nothing happened. A "Refining draft" badge in the
  // header indicates the in-flight state instead. Placeholder only
  // stays for the truly empty case (no content yet).
  const generatedItem = item.isGenerated ? item : null
  const showDocViewer = !!generatedItem || !!item.policyDocumentId

  const claimsSidebarRef = useRef<HTMLDivElement>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)

  const effectivePolicyDocId =
    generatedItem?.policyDocumentId ?? item.policyDocumentId
  const useCommentsBlockHighlight =
    enableComments &&
    isReadonlyMode &&
    viewingVersion === null &&
    Boolean(effectivePolicyDocId)
  const hasCommentsRail = Boolean(
    enableComments &&
    effectivePolicyDocId &&
    viewingVersion === null
  )
  // When a submissionMeta is supplied (auditor view) comments are
  // submission-scoped. If that submission doesn't exist yet, don't fall back to
  // the doc-scoped thread — that's the internal team's discussion.
  const commentsSubmissionId = submissionMeta?.submissionId ?? undefined
  const commentsEnabled =
    enableComments && (!submissionMeta || Boolean(commentsSubmissionId))
  const commentsController = useCommentsSyncController({
    docId: commentsEnabled ? (effectivePolicyDocId ?? undefined) : undefined,
    policySubmissionId: commentsEnabled ? commentsSubmissionId : undefined,
  })
  const docQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDocContent.queryOptions({
      policyDocumentId: effectivePolicyDocId ?? '',
    }),
    enabled: !!effectivePolicyDocId && showDocViewer,
    staleTime: 30_000,
    refetchInterval: isBackgroundDrafting ? 4000 : false,
    refetchOnWindowFocus: false,
  })
  const previousGenerationRef = useRef({
    policySlug: item.slug,
    inFlight: isBackgroundDrafting,
  })
  useEffect(() => {
    const previous = previousGenerationRef.current
    previousGenerationRef.current = {
      policySlug: item.slug,
      inFlight: isBackgroundDrafting,
    }
    if (
      previous.policySlug !== item.slug ||
      !previous.inFlight ||
      isBackgroundDrafting ||
      !effectivePolicyDocId
    ) {
      return
    }
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
          policyDocumentId: effectivePolicyDocId,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
          policyDocumentId: effectivePolicyDocId,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyClaims.queryKey({
          programId,
          policySlug: item.slug,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
          programId,
          policySlug: item.slug,
        }),
      }),
    ])
  }, [
    effectivePolicyDocId,
    isBackgroundDrafting,
    item.slug,
    programId,
    queryClient,
  ])
  const shouldUseSubmittedSnapshot =
    isReadonlyMode &&
    viewingVersion === null &&
    submissionMeta?.submittedVersion != null
  const displayedContent = shouldUseSubmittedSnapshot
    ? submittedVersionContent
    : viewingVersion !== null
      ? versionContent
      : docQuery.data
  const displayedContentLoading = shouldUseSubmittedSnapshot
    ? submittedVersionLoading
    : viewingVersion !== null
      ? versionLoading
      : docQuery.isLoading
  const displayedContentError = shouldUseSubmittedSnapshot
    ? submittedVersionError
    : viewingVersion !== null
      ? versionError
      : (docQuery.error?.message ?? null)
  const baselineVersion = shouldUseSubmittedSnapshot
    ? (submittedVersion ?? null)
    : (viewingVersion ?? item.currentVersion ?? item.approvedVersion ?? null)
  const baselineVersionLabel =
    shouldUseSubmittedSnapshot && submittedVersion != null
      ? `v${submittedVersion}`
      : viewingVersion !== null
        ? `v${viewingVersion}`
        : baselineVersion != null
          ? `v${baselineVersion} (Current)`
          : 'Current'
  const isCompareMode =
    compareVersion !== null && compareVersion !== baselineVersion
  const hasInvalidCompareSelection =
    compareVersion !== null && compareVersion === baselineVersion
  const compareOptions = useMemo(
    () =>
      [...versions]
        .filter((version) => version.version !== baselineVersion)
        .sort((a, b) => b.version - a.version),
    [baselineVersion, versions]
  )
  const defaultCompareVersion = compareOptions[0]?.version ?? null
  useEffect(() => {
    if (lastCompareDialogOpenRequestRef.current === compareDialogOpenRequest) {
      return
    }
    lastCompareDialogOpenRequestRef.current = compareDialogOpenRequest
    setCompareDialogManuallyOpen(true)
    onCompareVersionChange(defaultCompareVersion)
  }, [compareDialogOpenRequest, defaultCompareVersion, onCompareVersionChange])

  useEffect(() => {
    if (compareVersion !== null && compareVersion === baselineVersion) {
      onCompareVersionChange(null)
    }
  }, [baselineVersion, compareVersion, onCompareVersionChange])
  useEffect(() => {
    if (
      viewingVersion !== null ||
      shouldUseSubmittedSnapshot ||
      !effectivePolicyDocId ||
      !docQuery.data
    ) {
      return
    }
    onLiveContentLoaded?.(effectivePolicyDocId)
  }, [
    docQuery.data,
    effectivePolicyDocId,
    onLiveContentLoaded,
    shouldUseSubmittedSnapshot,
    viewingVersion,
  ])
  // Stable identity — only changes when the document or version being
  // viewed actually switches, NOT on every refetch. Previously this
  // included `docQuery.dataUpdatedAt`, which made the editor remount
  // every time an unrelated mutation invalidated the content query,
  // triggering TipTap/React DOM reconciliation conflicts.
  const displayedContentKey = shouldUseSubmittedSnapshot
    ? `submitted-version-${submissionMeta?.submittedVersion ?? 'none'}`
    : viewingVersion !== null
      ? `version-${viewingVersion}`
      : `${effectivePolicyDocId ?? 'none'}:current`

  // When viewing a historical policy version, scope claim queries to
  // that version so the sidebar/highlights reflect the snapshot the
  // user is reading rather than the latest draft.
  const versionScope =
    viewingVersion !== null && item.policyDocumentId
      ? {
          policyDocumentId: item.policyDocumentId,
          version: viewingVersion,
        }
      : null
  const claimsQuery = useQuery({
    ...trpc.claimTriage.list.queryOptions({
      programId,
      policyName: item.slug,
      ...(versionScope ?? {}),
    }),
    enabled:
      !!programId &&
      !claimsProp &&
      (viewingVersion === null || !!item.policyDocumentId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  // Draft claims — same source as generate-policies page. Only relevant
  // for the live draft (current version); skip for historical snapshots.
  const draftClaimsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDraftClaims.queryOptions({
      programId,
      policySlug: item.slug,
    }),
    enabled:
      !!programId && !!item.slug && !isReadonlyMode && viewingVersion === null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  const claimsMapQuery = useQuery({
    ...trpc.complianceProgram.getPolicyClaims.queryOptions({
      programId,
      policySlug: item.slug,
      ...(versionScope ?? {}),
    }),
    enabled:
      !!programId &&
      !!item.slug &&
      !claimsProp &&
      (viewingVersion === null || !!item.policyDocumentId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })

  // Draft assumptions (HITL) — blueprint gaps the background writer
  // filled with the recommended option. Shown as a confirm/dismiss
  // list in the sidebar.
  const assumptionsQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDraftAssumptions.queryOptions({
      programId,
      policySlug: item.slug,
    }),
    enabled: !!programId && !!item.slug && !isReadonlyMode,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  const { mutateAsync: resolveAssumption } = useMutation(
    trpc.complianceProgram.resolvePolicyDraftAssumption.mutationOptions()
  )
  const [resolvingAssumptionId, setResolvingAssumptionId] = useState<
    string | null
  >(null)
  const assumptions = useMemo(
    () => (assumptionsQuery.data?.assumptions ?? []) as any[],
    [assumptionsQuery.data?.assumptions]
  )
  const pendingAssumptions = useMemo(
    () => assumptions.filter((a) => a.status === 'pending'),
    [assumptions]
  )
  const [selectedAssumptionId, setSelectedAssumptionId] = useState<
    string | null
  >(null)
  const handleAssumptionDecision = useCallback(
    async (assumptionId: string, decision: 'confirmed' | 'dismissed') => {
      if (!programId) return
      setResolvingAssumptionId(assumptionId)
      try {
        await (resolveAssumption as any)({
          programId,
          policySlug: item.slug,
          assumptionId,
          decision,
        })
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDraftAssumptions.queryKey({
            programId,
            policySlug: item.slug,
          }),
        })
      } finally {
        setResolvingAssumptionId(null)
      }
    },
    [programId, item.slug, queryClient, resolveAssumption]
  )
  const handleAssumptionClick = useCallback(
    (assumption: { id: string; docBlockId: string | null }) => {
      setSelectedAssumptionId(assumption.id)
      if (!assumption.docBlockId) return
      const container = editorContainerRef.current
      if (!container) return
      const el = container.querySelector(
        `[data-policy-claim-block-id="${assumption.docBlockId}"], [id="${assumption.docBlockId}"], [data-dg-id="${assumption.docBlockId}"]`
      )
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    []
  )

  const {
    createDraftClaimMutation: _createDraftClaimMutation,
    updateDraftClaimMutation: _updateDraftClaimMutation,
    deleteDraftClaimMutation,
    invalidateDraftClaimQueries,
  } = usePolicyDraftClaimMutations({ programId, policySlug: item.slug })
  const { mutate: createPolicyClaim } = useMutation(
    trpc.complianceProgram.createPolicyClaim.mutationOptions()
  )

  const invalidateClaimQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyClaims.queryKey({
        programId,
        policySlug: item.slug,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.claimTriage.list.queryKey({
        programId,
        policyName: item.slug,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
        programId,
        policySlug: item.slug,
      }),
    })
  }, [queryClient, programId, item.slug])

  // Edit-claim was removed pending the model collapse — see GitHub issue
  // (PolicyClaim text as a derived view of the underlying policy block).
  // Until that lands, claim text is set at creation/regeneration time
  // only; users edit the policy passage directly in the editor.

  const handleClaimDelete = useCallback((claim: any) => {
    if (!claim || !claim.id) {
      console.error('[PolicyDetailPanel] Delete claim: missing claim id')
      return
    }
    // Show confirmation dialog for both draft and confirmed claims
    setClaimToDelete(claim)
  }, [])

  const handleAddClaim = useCallback(
    ({
      docBlockId,
      quoteText,
    }: {
      docBlockId: string
      quoteText: string
      offset: number
    }) => {
      const text = quoteText?.trim()
      if (!text || !effectivePolicyDocId) return
      ;(createPolicyClaim as any)(
        {
          programId,
          policySlug: item.slug,
          policyDocumentId: effectivePolicyDocId,
          docBlockId,
          quoteText: text,
        },
        {
          onSuccess: () => {
            invalidateClaimQueries()
            toast.success('Claim added')
          },
          onError: (err: unknown) => {
            const msg =
              err instanceof Error ? err.message : 'Failed to add claim'
            toast.error(msg)
          },
        }
      )
    },
    [
      createPolicyClaim,
      programId,
      item.slug,
      effectivePolicyDocId,
      invalidateClaimQueries,
    ]
  )

  const handleAskVera = useCallback(
    ({
      quoteText,
      docBlockId,
    }: {
      quoteText?: string
      docBlockId?: string
    }) => {
      setAssistantOpen(true)
      setChatMessage({
        text: quoteText
          ? `Help me with this passage:\n\n> ${quoteText}`
          : 'Help me with this policy',
        metadata: {
          ...(docBlockId && { blockId: docBlockId }),
          ...(quoteText && { quoteText }),
        },
      })
    },
    [setChatMessage, setAssistantOpen]
  )

  // Policy health / drift (#994). Queried at the parent so the tab can show a
  // finding count; the Health tab body re-uses the same queryKey (TanStack
  // dedupes → one request).
  const policyHealthQuery = useQuery({
    ...trpc.complianceProgram.getPolicyHealth.queryOptions({
      programId,
      policySlug: item.slug,
      policyDocumentId: effectivePolicyDocId ?? undefined,
    }),
    enabled: !isReadonlyMode && !!programId && !!item.slug,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  })
  // Health shows test status only (2026-09-30): the badge counts the tests
  // that need attention.
  const healthFindingCount =
    (policyHealthQuery.data?.testHealth?.tests.filter((test) =>
      ['FAILED', 'NEEDS_RETEST', 'BLOCKED', 'INCONCLUSIVE'].includes(
        test.effectiveStatus
      )
    ).length ?? 0)

  const {
    mutateAsync: deleteConfirmedClaim,
    isPending: isDeleteConfirmedClaimPending,
  } = useMutation(trpc.complianceProgram.deleteConfirmedClaim.mutationOptions())

  const handleConfirmClaimDelete = useCallback(async () => {
    if (!claimToDelete) return
    try {
      // Check if it's a draft claim or confirmed claim
      if (claimToDelete.draftClaimId) {
        // Delete draft claim
        await (deleteDraftClaimMutation.mutateAsync as any)({
          programId,
          policySlug: item.slug,
          draftClaimId: claimToDelete.draftClaimId,
        })
        await invalidateDraftClaimQueries()
      } else {
        // Delete confirmed claim
        await (deleteConfirmedClaim as any)({
          programId,
          claimId: claimToDelete.id,
        })
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyClaims.queryKey({
            programId,
            policySlug: item.slug,
          }),
        })
        queryClient.invalidateQueries({
          queryKey: trpc.claimTriage.list.queryKey({
            programId,
            policyName: item.slug,
          }),
        })
        if (effectivePolicyDocId) {
          queryClient.invalidateQueries({
            queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
              policyDocumentId: effectivePolicyDocId,
            }),
          })
        }
        await invalidateDraftClaimQueries()
      }
      setClaimToDelete(null)
    } catch (error) {
      console.error('[PolicyDetailPanel] Delete claim failed:', error)
      const message =
        error instanceof Error ? error.message : 'Failed to delete claim'
      console.error('[PolicyDetailPanel] Error details:', message)
    }
  }, [
    claimToDelete,
    deleteDraftClaimMutation,
    deleteConfirmedClaim,
    programId,
    item.slug,
    effectivePolicyDocId,
    invalidateDraftClaimQueries,
    queryClient,
  ])

  const triageClaims = useMemo(
    () => claimsProp ?? ((claimsQuery.data ?? []) as TriageClaim[]),
    [claimsProp, claimsQuery.data]
  )
  const draftClaims = useMemo(
    () => (draftClaimsQuery.data?.claims ?? []) as any[],
    [draftClaimsQuery.data]
  )
  const claims = useMemo(
    () =>
      [...triageClaims].sort((a, b) => {
        if (a.needsReview && !b.needsReview) return -1
        if (!a.needsReview && b.needsReview) return 1
        return 0
      }),
    [triageClaims]
  )
  const highlightClaims = useMemo(() => {
    if (claimsProp) {
      return claimsProp.map((claim) => ({
        id: claim.id,
        docBlockId: claim.docBlockId,
      })) as PolicyClaimHighlight[]
    }

    const mapClaims = (claimsMapQuery.data?.claims ??
      []) as PolicyClaimHighlight[]
    if (mapClaims.length > 0) return mapClaims
    // Fall back to draft claims for highlight mapping
    return draftClaims
      .filter((c: any) => !!c.docBlockId)
      .map((c: any) => ({
        id: c.id,
        docBlockId: c.docBlockId,
      })) as PolicyClaimHighlight[]
  }, [claimsMapQuery.data, claimsProp, draftClaims])
  const useDraftClaims =
    !isReadonlyMode && claims.length === 0 && draftClaims.length > 0
  const hasAssumptionsSidebar = assumptions.length > 0
  const claimsVisible = showClaims ?? (showSidebar && sidebarTab === 'claims')
  const topLevelComments = useMemo(
    () => commentsController.comments.filter((comment) => !comment.parentId),
    [commentsController.comments]
  )
  const openCommentThreadCount = useMemo(
    () =>
      topLevelComments.filter(
        (comment) =>
          !['RESOLVED', 'DISMISSED', 'IMPLEMENTED'].includes(
            comment.commentStatus
          )
      ).length,
    [topLevelComments]
  )
  const { claimIdToBlockId, blockIdToClaimIds } = useMemo(
    () => buildPolicyClaimBlockMaps(highlightClaims),
    [highlightClaims]
  )
  const claimPluginKeyRef = useRef(
    new PluginKey('screenataPolicyDetailsClaimDecorations')
  )

  const commitmentCount = claims.length

  /** Rail → document. The editor highlight is already driven by
   *  `selectedClaimId`; this only adds the scroll. */
  const handleCommitmentClaimSelect = useCallback(
    (claimId: string) => {
      setSelectedClaimId(claimId)
      scrollToPolicyClaimBlock(
        editorContainerRef.current,
        claimIdToBlockId,
        claimId
      )
    },
    [claimIdToBlockId]
  )

  const openClaimsSidebar = useCallback(() => {
    setShowSidebar(true)
    setSidebarTab('claims')
    onSidebarTabChange?.('claims')
    onShowClaimsChange?.(true)
  }, [onSidebarTabChange, onShowClaimsChange])

  useEffect(() => {
    if (!focusedClaimId) return

    openClaimsSidebar()
    setSelectedClaimId(focusedClaimId)
    const frame = requestAnimationFrame(() => {
      const blockId =
        focusedClaimBlockId ?? claimIdToBlockId[focusedClaimId] ?? null
      if (blockId) {
        scrollToPolicyClaimBlock(
          editorContainerRef.current,
          { [focusedClaimId]: blockId },
          focusedClaimId
        )
      }
      const claimEl = Array.from(
        claimsSidebarRef.current?.querySelectorAll('[data-claim-id]') ?? []
      ).find(
        (element) => element.getAttribute('data-claim-id') === focusedClaimId
      )
      claimEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })

    return () => cancelAnimationFrame(frame)
  }, [
    claimIdToBlockId,
    editorMountCount,
    focusedClaimBlockId,
    focusedClaimId,
    openClaimsSidebar,
  ])

  const openCommentsSidebar = useCallback(() => {
    if (!hasCommentsRail) return
    setShowSidebar(true)
    setSidebarTab('comments')
    onSidebarTabChange?.('comments')
    onShowClaimsChange?.(false)
  }, [hasCommentsRail, onSidebarTabChange, onShowClaimsChange])

  const handleCommentActivated = useCallback(
    (commentId: string | null) => {
      if (!commentId) return
      openCommentsSidebar()
      setActiveCommentId(commentId)
      setActiveCommentSource('editor')
    },
    [openCommentsSidebar]
  )

  const handleCommentsError = useCallback((message: string) => {
    toast.error(message)
  }, [])

  const openAssumptionsSidebar = useCallback(() => {
    setShowSidebar(true)
    setSidebarTab('assumptions')
    onSidebarTabChange?.('assumptions' as any)
    onShowClaimsChange?.(false)
  }, [onSidebarTabChange, onShowClaimsChange])

  // Unified top-level tab handler. Properties + Claims are always present;
  // keep showClaims
  // in sync so the editor's active-claim highlight follows the Claims tab.
  const handleSidebarTabChange = useCallback(
    (value: string) => {
      const tab = value as
        | 'properties'
        | 'claims'
        | 'comments'
        | 'assumptions'
        | 'health'
      setShowSidebar(true)
      setSidebarTab(tab)
      if (tab !== 'health') onSidebarTabChange?.(tab)
      onShowClaimsChange?.(tab === 'claims')
    },
    [onSidebarTabChange, onShowClaimsChange]
  )

  useEffect(() => {
    onOpenCommentThreadCountChange?.(openCommentThreadCount)
  }, [onOpenCommentThreadCountChange, openCommentThreadCount])

  useEffect(() => {
    if (preferredSidebarTab === 'comments' && hasCommentsRail) {
      setShowSidebar(true)
      setSidebarTab('comments')
      return
    }
    if (preferredSidebarTab === 'claims' && showClaims !== false) {
      setShowSidebar(true)
      setSidebarTab('claims')
    }
  }, [preferredSidebarTab, hasCommentsRail, showClaims])

  useEffect(() => {
    if (showClaims === undefined) return

    if (showClaims) {
      if (preferredSidebarTab === 'comments' && hasCommentsRail) return
      setShowSidebar(true)
      setSidebarTab('claims')
      return
    }

    setSelectedClaimId(null)

    // Panel is non-closable: if claims were toggled off while the Claims tab
    // was active, fall back to Properties instead of hiding the sidebar.
    if (showSidebar && sidebarTab === 'claims') {
      setSidebarTab('properties')
    }
  }, [
    showClaims,
    showSidebar,
    sidebarTab,
    preferredSidebarTab,
    hasCommentsRail,
  ])

  // Properties + Claims are always-present tabs. If the active *conditional*
  // tab (Comments / Assumptions) loses its content, fall back to Properties.
  useEffect(() => {
    if (sidebarTab === 'comments' && !hasCommentsRail) {
      setSidebarTab('properties')
    } else if (sidebarTab === 'assumptions' && !hasAssumptionsSidebar) {
      setSidebarTab('properties')
    }
  }, [sidebarTab, hasCommentsRail, hasAssumptionsSidebar])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor || editor.isDestroyed) return
    // Ordinary readonly views keep the document clean. Auditor policy views may
    // render text highlights, but claim markers and edit actions stay disabled.
    if (!canRenderClaimDecorations) {
      try {
        editor.unregisterPlugin(claimPluginKeyRef.current)
        if (editor.view?.docView) {
          editor.view.dispatch(editor.state.tr)
        }
      } catch {
        /* ignore */
      }
      return
    }
    const hasAnyDecoratableClaims = useDraftClaims
      ? draftClaims.some((c: any) => !!c.docBlockId)
      : claims.some((claim) => !!claim.docBlockId)
    if (!hasAnyDecoratableClaims) {
      try {
        editor.unregisterPlugin(claimPluginKeyRef.current)
        if (editor.view?.docView) {
          editor.view.dispatch(editor.state.tr)
        }
      } catch {
        /* ignore */
      }
      return
    }

    const decorationClaims = useDraftClaims
      ? draftClaims
          .filter((c: any) => !!c.docBlockId)
          .map((c: any) => ({
            id: c.id,
            docBlockId: c.docBlockId,
            claimText: c.claimText,
            quoteText: c.quoteText ?? null,
            verification: c.verification,
            confidence: c.confidence,
            triageStatus: c.triageStatus,
            needsReview: !!c.needsReview,
            hasLinkedTests:
              (c.linkedTests?.length ?? 0) > 0 ||
              (c.claimTestLinks?.length ?? 0) > 0 ||
              !!c.hasCreatedTest,
          }))
      : claims
          .filter((claim) => !!claim.docBlockId)
          .map((claim) => ({
            id: claim.id,
            docBlockId: claim.docBlockId,
            claimText: claim.claimText,
            quoteText: (claim as any).quoteText ?? null,
            verification: claim.verification,
            confidence: claim.confidence,
            triageStatus: (claim as any).triageStatus ?? null,
            needsReview: !!(claim as any).needsReview,
            hasLinkedTests:
              ((claim as any).linkedTests?.length ?? 0) > 0 ||
              ((claim as any).claimTestLinks?.length ?? 0) > 0 ||
              !!(claim as any).hasCreatedTest,
          }))

    return registerPolicyClaimBadgeDecorations({
      editor,
      pluginKey: claimPluginKeyRef.current,
      claims: decorationClaims,
      activeClaimId: claimsVisible ? selectedClaimId : null,
      showHighlights,
      showClaimMarkers: !isReadonlyMode,
      onClaimClick: (claimId) => {
        setSelectedClaimId(claimId)
        // Open the claims panel via the parent so the meta bar toggle stays in sync.
        openClaimsSidebar()
        requestAnimationFrame(() => {
          const claimEl = claimsSidebarRef.current?.querySelector(
            `[data-claim-id="${claimId}"]`
          )
          claimEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      },
      onClaimDelete: canTriageClaims ? handleClaimDelete : undefined,
      editable: canTriageClaims,
    })
  }, [
    claims,
    draftClaims,
    useDraftClaims,
    selectedClaimId,
    editorMountCount,
    claimsVisible,
    editorRef,
    canTriageClaims,
    handleClaimDelete,
    openClaimsSidebar,
    showHighlights,
    canRenderClaimDecorations,
    isReadonlyMode,
  ])

  const claimInfoMap = useMemo(() => {
    const map: Record<
      string,
      { templateKey?: string | null; triageStatus?: string | null }
    > = {}
    const source = useDraftClaims ? draftClaims : claims
    for (const claim of source) {
      map[claim.id] = {
        templateKey: (claim as any).templateKey,
        triageStatus: (claim as any).triageStatus,
      }
    }
    return map
  }, [claims, draftClaims, useDraftClaims])

  useEffect(() => {
    const container = editorContainerRef.current
    if (
      !container ||
      !claimsVisible ||
      isReadonlyMode ||
      Object.keys(blockIdToClaimIds).length === 0
    )
      return

    if (useCommentsBlockHighlight) {
      return syncPolicyClaimBlockTargets(container, blockIdToClaimIds)
    }

    return syncPolicyClaimBlockDecorations(
      container,
      blockIdToClaimIds,
      selectedClaimId ? claimIdToBlockId[selectedClaimId] : null,
      claimInfoMap
    )
  }, [
    blockIdToClaimIds,
    claimIdToBlockId,
    selectedClaimId,
    claimInfoMap,
    claimsVisible,
    useCommentsBlockHighlight,
    isReadonlyMode,
  ])

  useEffect(() => {
    const handler = (e: Event) => {
      const policyDocumentId = (e as CustomEvent).detail?.policyDocumentId
      if (!policyDocumentId) return
      // Parent route handles query invalidation after stream finish.
      // This panel only needs the latest policy document id.
      onPolicyDocChange?.(policyDocumentId)
    }
    window.addEventListener('screenata:policy-generated', handler)
    return () =>
      window.removeEventListener('screenata:policy-generated', handler)
  }, [onPolicyDocChange])

  useEffect(() => {
    // When the host already wires `usePolicyEditorEvents` (which owns the
    // approved→draft branching), skip this redundant listener so it can't
    // mutate an approved version in parallel.
    if (isReadonlyMode || disableApplyBlockListener) return

    const handler = (e: Event) => {
      const { blockId, action, content } = (e as CustomEvent).detail ?? {}
      if (!editorRef.current || !blockId) return
      const editor = editorRef.current
      const wasEditable = editor.isEditable
      if (!wasEditable) editor.setEditable(true)
      try {
        applyBlockToEditor(editor, blockId, action, content)
      } finally {
        if (!wasEditable) editor.setEditable(false)
      }
    }
    const legacyHandler = (e: Event) => {
      const content = (e as CustomEvent).detail?.content
      if (!content || !editorRef.current) return
      const editor = editorRef.current
      const wasEditable = editor.isEditable
      if (!wasEditable) editor.setEditable(true)
      try {
        const { content: parsed } = deserializeFromMarkdown(content)
        editor.chain().focus().setContent(parsed).run()
      } catch {
        try {
          editor.commands.setContent(content)
        } catch {
          /* ignore */
        }
      } finally {
        if (!wasEditable) editor.setEditable(false)
      }
    }
    window.addEventListener('screenata:apply-block', handler)
    window.addEventListener('screenata:apply-to-editor', legacyHandler)
    return () => {
      window.removeEventListener('screenata:apply-block', handler)
      window.removeEventListener('screenata:apply-to-editor', legacyHandler)
    }
  }, [isReadonlyMode, disableApplyBlockListener])

  useEffect(() => {
    const container = editorContainerRef.current
    if (
      !container ||
      !claimsVisible ||
      Object.keys(blockIdToClaimIds).length === 0
    )
      return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const resolveBlockIdFromTarget = () => {
        let current: HTMLElement | null = target

        while (current && current !== container) {
          const taggedBlockId = current.getAttribute(
            'data-policy-claim-block-id'
          )
          if (taggedBlockId && blockIdToClaimIds[taggedBlockId]?.length) {
            return taggedBlockId
          }

          const domBlockId =
            current.getAttribute('id') ??
            current.getAttribute('data-id') ??
            current.getAttribute('data-dg-id')

          if (domBlockId && blockIdToClaimIds[domBlockId]?.length) {
            return domBlockId
          }

          current = current.parentElement
        }

        return null
      }

      const directClaimId = target
        .closest('[data-claim-id]')
        ?.getAttribute('data-claim-id')
      if (directClaimId) {
        setSelectedClaimId(directClaimId)
        requestAnimationFrame(() => {
          const claimEl = claimsSidebarRef.current?.querySelector(
            `[data-claim-id="${directClaimId}"]`
          )
          claimEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
        return
      }
      const blockEl = target.closest(
        '[data-policy-claim-block-id]'
      ) as HTMLElement | null
      const blockId =
        blockEl?.getAttribute('data-policy-claim-block-id') ??
        resolveBlockIdFromTarget()
      if (!blockId) {
        setSelectedClaimId(null)
        return
      }
      const claimId = blockIdToClaimIds[blockId]?.[0] ?? null
      if (claimId) {
        setSelectedClaimId(claimId)
        requestAnimationFrame(() => {
          const claimEl = claimsSidebarRef.current?.querySelector(
            `[data-claim-id="${claimId}"]`
          )
          claimEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      }
    }

    container.addEventListener('click', handleClick)
    return () => container.removeEventListener('click', handleClick)
  }, [blockIdToClaimIds, claimsVisible, editorRef])

  useEffect(() => {
    if (!useCommentsBlockHighlight || !claimsVisible || !selectedClaimId) {
      editorRef.current?.commands?.clearBlockHighlight?.()
      return
    }

    const blockId = claimIdToBlockId[selectedClaimId]
    if (!blockId) {
      editorRef.current?.commands?.clearBlockHighlight?.()
      return
    }

    editorRef.current?.commands?.highlightClaimBlock?.(blockId)
  }, [
    claimsVisible,
    claimIdToBlockId,
    editorRef,
    selectedClaimId,
    useCommentsBlockHighlight,
  ])

  const handleCommentClick = useCallback(
    (comment: any) => {
      openCommentsSidebar()
      setActiveCommentId(comment.id)
      setActiveCommentSource('panel')
      const editor = editorRef.current
      if (!editor) return

      if (comment.scope === 'RANGE') {
        editor.commands.openComment?.(comment.id)
        const commentMark = Array.from(
          editorContainerRef.current?.querySelectorAll('[data-comment-id]') ??
            []
        ).find(
          (element) => element.getAttribute('data-comment-id') === comment.id
        )
        commentMark?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      if (comment.scope === 'BLOCK' && comment.docBlockId) {
        editor.commands.highlightBlock?.(comment.docBlockId)
      }
    },
    [editorRef, openCommentsSidebar]
  )

  const hasContent = hasRenderablePolicyContent(
    displayedContent?.tiptapJson,
    displayedContent?.content
  )
  const hasHighlightableClaims = (useDraftClaims ? draftClaims : claims).some(
    (claim) => Boolean(claim.docBlockId)
  )

  // Highlights toggle — relocated here from the (removed) top metadata bar.
  // Only meaningful when the doc has claims to highlight in the editor.
  const highlightsToggle =
    canRenderClaimDecorations && onShowHighlightsChange && hasHighlightableClaims ? (
      <button
        type='button'
        onClick={() => onShowHighlightsChange(!showHighlights)}
        aria-pressed={!!showHighlights}
        title={
          showHighlights ? 'Hide claim highlights' : 'Show claim highlights'
        }
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[11px] font-medium transition-colors',
          showHighlights
            ? 'border-ring bg-selected text-ring'
            : 'border-border text-text-subtle hover:bg-muted'
        )}
      >
        <Highlighter className='h-3.5 w-3.5' />
        Highlight
      </button>
    ) : null

  const sidebarTabsListCls = 'flex w-full min-w-0 gap-0 overflow-hidden'
  const sidebarTabTriggerCls =
    'h-9 min-w-0 flex-1 justify-center gap-1 px-1.5 text-[11px]'
  const compareDialogOpen =
    compareDialogManuallyOpen || isCompareMode || hasInvalidCompareSelection
  const compareDialog = (
    <Dialog
      open={compareDialogOpen}
      onOpenChange={(open) => {
        setCompareDialogManuallyOpen(open)
        if (!open) {
          onCompareVersionChange(null)
        }
      }}
    >
      <DialogContent className='flex !h-[95vh] !w-[95vw] !max-w-[95vw] flex-col gap-0 overflow-hidden p-0'>
        <DialogHeader className='border-border border-b px-6 py-4 pr-12'>
          <DialogTitle className='text-text-secondary shrink-0 text-[14px] font-semibold'>
            Compare policy versions
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Compare two policy versions.
          </DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex-1 overflow-hidden bg-[#F5F6F8] p-4 dark:bg-background'>
          <div className='h-full min-h-0'>
            {hasInvalidCompareSelection ? (
              <div className='border-border-divider flex h-full min-h-[260px] items-center justify-center rounded-sm border border-dashed bg-card px-6 text-center'>
                <div>
                  <GitCompareArrows className='text-text-placeholder mx-auto h-5 w-5' />
                  <p className='text-text-secondary mt-2 text-[13px] font-medium'>
                    Select a different version to compare.
                  </p>
                  <p className='text-text-subtle mt-1 text-[12px]'>
                    A policy version cannot be compared with itself.
                  </p>
                </div>
              </div>
            ) : (
              <TiptapDiffView
                previous={compareVersionContent?.tiptapJson ?? null}
                current={displayedContent?.tiptapJson ?? null}
                previousLabel={
                  compareVersion ? `v${compareVersion}` : 'Select version'
                }
                currentLabel={baselineVersionLabel}
                layout='current-first'
                viewMode={compareViewMode}
                controls={
                  <div className='flex w-full flex-wrap items-center justify-between gap-3'>
                    <div className='flex flex-wrap items-center gap-2'>
                      <SearchableSelect
                        value={
                          compareVersion ? String(compareVersion) : undefined
                        }
                        onValueChange={(value) =>
                          onCompareVersionChange(Number(value))
                        }
                        disabled={compareOptions.length === 0}
                        options={compareOptions.map((version) => ({
                          label: `v${version.version}${
                            version.approvalSignature
                              ? ' (Approved)'
                              : ' (Draft)'
                          }`,
                          value: String(version.version),
                        }))}
                        placeholder={
                          compareOptions.length === 0
                            ? 'No other versions'
                            : 'Select version'
                        }
                        searchPlaceholder='Search versions...'
                        emptyMessage='No other versions found.'
                        triggerProps={{
                          'aria-label': 'Compare with version',
                          className:
                            'border-border-divider text-text-secondary h-8 w-[170px] bg-card px-2 text-[12px] font-medium shadow-none',
                        }}
                      />
                    </div>
                    <div className='text-text-body ml-auto inline-flex h-8 items-center gap-2 text-[12px] font-medium'>
                      <span>Inline</span>
                      <Switch
                        aria-label='Show inline diff'
                        checked={compareViewMode === 'inline'}
                        onCheckedChange={(checked) =>
                          setCompareViewMode(
                            checked ? 'inline' : 'side-by-side'
                          )
                        }
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type='button'
                            aria-label='About inline comparison'
                            className='inline-flex h-6 w-6 cursor-help items-center justify-center text-status-orange transition-colors hover:text-status-orange'
                          >
                            <Info className='h-3.5 w-3.5' />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side='bottom' className='max-w-[300px]'>
                          Inline shows additions and deletions in one document
                          instead of side by side.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                }
                loading={displayedContentLoading || compareVersionLoading}
                error={displayedContentError || compareVersionError}
                className='mx-auto h-full max-w-none'
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )

  return (
    <div className='bg-background relative flex min-h-0 w-full flex-1 flex-col'>
      {compareDialog}
      <div className='flex flex-1 overflow-hidden'>
        <div className='flex flex-1 flex-col overflow-hidden'>
          {/* Main-column banner (e.g. the AI-draft review notice). Lives inside
              the editor column so the strip doesn't span over the right
              Properties/Claims panel. */}
          {mainBanner && <div className='shrink-0'>{mainBanner}</div>}
          {claims.some((claim) => !claim.docBlockId) && (
            <p role='status' className='shrink-0 border-b border-border bg-surface-subtle px-6 py-2 text-xs text-status-orange'>
              {claims.filter((claim) => !claim.docBlockId).length} claim sources need review. Their stored assertions could not be located in this policy version.
            </p>
          )}
          {viewingVersion === null && submissionMetaBar && (
            <div className='border-border bg-surface-subtle shrink-0 border-b px-6 py-3'>
              {submissionMetaBar}
            </div>
          )}
          {viewingVersion !== null && !isEditing && (
            <div className='flex shrink-0 items-center justify-between border-b border-status-orange/30 bg-status-orange-bg px-6 py-2 text-[12px] text-status-orange-strong'>
              <div className='flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1'>
                <div className='flex shrink-0 items-center gap-2'>
                  <History className='h-3.5 w-3.5 shrink-0' />
                  Viewing version {viewingVersion} (read-only snapshot)
                </div>
                {submissionMetaBar}
              </div>
              {!isReadonlyMode && (
                <div className='flex items-center gap-2'>
                  <button
                    type='button'
                    onClick={() => {
                      setCompareDialogManuallyOpen(true)
                      onCompareVersionChange(defaultCompareVersion)
                    }}
                    className='border-border text-text-body hover:bg-muted inline-flex items-center gap-1.5 rounded-sm border bg-card px-2.5 py-1 text-[11px] font-medium transition-colors'
                  >
                    <GitCompareArrows className='h-3 w-3' />
                    Compare versions
                  </button>
                  <button
                    onClick={onRevert}
                    disabled={revertPending}
                    className='inline-flex items-center gap-1.5 rounded-sm border border-amber-300 bg-card px-2.5 py-1 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:border-status-orange/30 dark:bg-status-orange-bg dark:text-status-orange-strong dark:hover:bg-status-orange-bg/80'
                  >
                    {revertPending ? (
                      <Loader2 className='h-3 w-3 animate-spin' />
                    ) : (
                      <RefreshCw className='h-3 w-3' />
                    )}
                    Revert to this version
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Assumptions toggle row */}
          {showDocViewer && hasAssumptionsSidebar && (
            <div className='border-border flex shrink-0 items-center justify-end border-b bg-card'>
              {!(showSidebar && sidebarTab === 'assumptions') && (
                <button
                  type='button'
                  onClick={openAssumptionsSidebar}
                  className={`mr-2 inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm px-2.5 text-[12px] font-medium transition-colors ${
                    pendingAssumptions.length > 0
                      ? 'bg-status-orange-bg text-status-orange hover:bg-status-orange-bg/80'
                      : 'text-text-subtle hover:bg-muted'
                  }`}
                >
                  <Sparkles className='h-3.5 w-3.5' />
                  Assumptions ({pendingAssumptions.length}
                  {assumptions.length > pendingAssumptions.length
                    ? `/${assumptions.length}`
                    : ''}
                  )
                </button>
              )}
            </div>
          )}

          {showDocViewer && displayedContentLoading ? (
            <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
              <PolicyDetailDocumentSkeleton
                embedded
                embedInOverlay={embedInOverlay}
              />
            </div>
          ) : (
            <ScrollArea
              className='min-h-0 flex-1 overflow-hidden'
              viewClassName='[&>div]:!block [&>div]:h-full'
            >
              <div className='h-full'>
                {!isReadonlyMode && !hasContent && !displayedContentError && isBackgroundDrafting && (
                  <div className='bg-muted flex min-h-full justify-center px-20 py-10'>
                    <div className='border-border-divider flex min-h-[calc(var(--app-vh)_-_170px)] w-full max-w-[816px] items-center justify-center rounded-sm border border-dashed bg-card px-16 py-12'>
                      <div className='mx-auto max-w-[460px] text-center'>
                        <Loader2 className='text-primary mx-auto size-10 animate-spin' />
                        <h3 className='font-heading text-foreground mt-4 text-[20px] leading-[1.25] font-semibold tracking-[-0.01em]'>
                          {item.title}
                        </h3>
                        <p className='text-muted-foreground mx-auto mt-2 max-w-[400px] text-[13px] leading-5'>
                          The draft appears here as soon as it is written,
                          usually within a minute or two. You can leave this page; it
                          keeps writing.
                        </p>
                        <p className='text-text-placeholder mx-auto mt-1 max-w-[440px] text-[13px] leading-5'>
                          {item.generationState === 'queued'
                            ? 'Queued for generation'
                            : `Vera is drafting this ${item.layer === 'narrative' ? 'document' : 'policy'}`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!isReadonlyMode && !hasContent && !displayedContentError && !isBackgroundDrafting && (
                  <div className='bg-muted flex min-h-full justify-center px-20 py-10'>
                    <div className='border-border-divider flex min-h-[calc(var(--app-vh)_-_170px)] w-full max-w-[816px] items-center justify-center rounded-sm border border-dashed bg-card px-16 py-12'>
                      <div className='mx-auto max-w-[460px] text-center'>
                        <FileText className='text-text-placeholder mx-auto size-10' />
                        <h3 className='font-heading text-foreground mt-4 text-[20px] leading-[1.25] font-semibold tracking-[-0.01em]'>
                          {item.title}
                        </h3>
                        {/* An out-of-scope policy is blank because setup
                            declined to draft it, not because generation is
                            pending. Saying "Generate to create the first
                            draft" here reads as a step the user forgot. */}
                        <p className='text-muted-foreground mx-auto mt-2 max-w-[400px] text-[13px] leading-5'>
                          {item.inScope === false
                            ? `${item.outOfScopeReason ?? `This ${item.layer === 'narrative' ? 'document' : 'policy'} is not required by your program's scope`}, so Vera did not draft it. Start it anyway if your scope has changed.`
                            : `Upload the ${item.layer === 'narrative' ? 'document' : 'policy'} you already have, or answer a few questions and let Vera draft it.`}
                        </p>

                        {/* Two ways in: bring your own document, or answer the
                            policy's own questions and generate from the
                            template. Generation opens the questionnaire first —
                            the answers are what make the draft yours. */}
                        <div className='mt-5 flex flex-wrap justify-center gap-2'>
                          <button
                            type='button'
                            onClick={() => setUploadOpen(true)}
                            className='border-border-divider text-text-secondary hover:bg-surface-subtle inline-flex items-center gap-1.5 rounded-sm border bg-card px-3.5 py-2 text-[12px] font-medium transition-colors'
                          >
                            <Upload className='size-3.5' />
                            Upload existing {item.layer === 'narrative' ? 'document' : 'policy'}
                          </button>
                          <button
                            type='button'
                            onClick={() => setQuestionnaireOpen(true)}
                            className='bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1.5 rounded-sm px-3.5 py-2 text-[12px] font-medium transition-colors'
                          >
                            <Sparkles className='size-3.5' />
                            Generate with Vera
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showDocViewer &&
                  (isReadonlyMode || hasContent || !!displayedContentError) && (
                  <>
                    {!displayedContentLoading &&
                      displayedContentError &&
                      !hasContent && (
                        <div className='bg-muted flex min-h-full justify-center px-20 py-10'>
                          <div className='w-full max-w-[816px] rounded-sm border border-status-orange/30 bg-status-orange-bg px-6 py-5 text-status-orange-strong shadow-[0_2px_12px_rgba(0,0,0,0.06)]'>
                            <div className='flex items-start gap-3'>
                              <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-status-orange' />
                              <div>
                                <p className='text-sm font-semibold'>
                                  Failed to load this policy version
                                </p>
                                <p className='mt-1 text-sm leading-6 text-status-orange-strong'>
                                  {displayedContentError}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    <EditorErrorBoundary className='h-full'>
                      {!displayedContentLoading &&
                        (!displayedContentError || hasContent) && (
                          <div className='bg-muted flex min-h-full justify-center px-20 py-10'>
                            <PolicyContentViewer
                              key={displayedContentKey}
                              plateContent={displayedContent?.tiptapJson}
                              contentMarkdown={displayedContent?.content}
                              isLoading={displayedContentLoading}
                              contentKey={displayedContentKey}
                              readOnly={!isEditing}
                              reviewMode={!isEditing}
                              contentSyncMode={
                                isEditing ? 'initial-only' : 'reactive'
                              }
                              enableComments={
                                commentsEnabled &&
                                viewingVersion === null &&
                                Boolean(effectivePolicyDocId)
                              }
                              hideCommentComposer={hideCommentComposer}
                              showSelectionToolbar={true}
                              docId={
                                viewingVersion === null
                                  ? (effectivePolicyDocId ?? undefined)
                                  : undefined
                              }
                              workspaceId={workspaceId}
                              policySubmissionId={
                                isReadonlyMode && viewingVersion === null
                                  ? commentsSubmissionId
                                  : undefined
                              }
                              editorContainerRef={editorContainerRef}
                              onCreate={({ editor }) => {
                                editorRef.current = editor
                                onEditorCreate?.({
                                  editor,
                                  onOpenComments: hasCommentsRail ? openCommentsSidebar : undefined,
                                  onAskVera: handleAskVera,
                                  onAddClaimFromSelection:
                                    viewingVersion === null &&
                                    Boolean(effectivePolicyDocId) &&
                                    (canTriageClaims || isEditing)
                                      ? handleAddClaim
                                      : undefined,
                                })
                                commentsController.attachEditor(editor)
                                setEditorMountCount((count) => count + 1)
                                const outline = extractBlockOutline(
                                  editor.getJSON()
                                )
                                setBlockOutline(formatBlockOutline(outline))
                              }}
                              onUpdate={onEditorUpdate}
                              onCommentsUpdated={
                                commentsController.mergeFromEditor
                              }
                              onCommentsDataUpdated={
                                commentsController.mergeFromEditor
                              }
                              onCommentsError={handleCommentsError}
                              onAddClaimFromSelection={
                                viewingVersion === null &&
                                Boolean(effectivePolicyDocId) &&
                                (canTriageClaims || isEditing)
                                  ? handleAddClaim
                                  : undefined
                              }
                              onAskVera={handleAskVera}
                              onCommentActivated={handleCommentActivated}
                              emptyTitle='Ready to generate policy.'
                              emptyDescription='Generate the policy to create the first draft.'
                              className={cn(
                                'w-full max-w-[816px] rounded-sm bg-card px-16 py-12 shadow-[0_2px_12px_rgba(0,0,0,0.06)] [&_.editor-container]:!pt-0 [&_.editor-content]:!bg-card',
                                embedInOverlay
                                  ? 'min-h-[480px] [&_.tiptap.ProseMirror]:min-h-[360px]'
                                  : 'min-h-[calc(var(--app-vh)_-_200px)] [&_.tiptap.ProseMirror]:min-h-[calc(var(--app-vh)_-_280px)]'
                              )}
                              editorClassName='mt-0 mb-0'
                            />
                          </div>
                        )}
                    </EditorErrorBoundary>
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Right sidebar — always available (Properties tab always present;
            Claims / Comments / Assumptions appear when they have content).
            Rendered statically (no enter animation) since the panel can't be
            closed; a fixed width-96 column. */}
        {showSidebar && (
          <div
            ref={claimsSidebarRef}
            className='border-border w-96 shrink-0 overflow-hidden border-l bg-card'
          >
            <Tabs
              value={sidebarTab}
              onValueChange={handleSidebarTabChange}
              className='flex h-full min-h-0 w-96 min-w-96 flex-col gap-0 overflow-hidden'
            >
              <div className='sticky top-0 z-10 shrink-0 overflow-hidden bg-card'>
                <div className='flex items-center px-2'>
                  <TabsList className={sidebarTabsListCls}>
                    <TabsTrigger
                      value='properties'
                      className={sidebarTabTriggerCls}
                    >
                      <SlidersHorizontal className='h-3.5 w-3.5' />
                      Info
                    </TabsTrigger>
                    <TabsTrigger
                      value='claims'
                      className={sidebarTabTriggerCls}
                    >
                      <Shield className='h-3.5 w-3.5' />
                      Claims
                      {commitmentCount > 0 && ` (${commitmentCount})`}
                    </TabsTrigger>
                    {!isReadonlyMode && (
                    <TabsTrigger
                      value='health'
                      className={sidebarTabTriggerCls}
                    >
                      <Activity className='h-3.5 w-3.5' />
                      Health
                      {healthFindingCount > 0 && (
                        <span className='ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-700'>
                          {healthFindingCount}
                        </span>
                      )}
                    </TabsTrigger>
                    )}
                    {hasCommentsRail && (
                      <TabsTrigger
                        value='comments'
                        className={sidebarTabTriggerCls}
                      >
                        <MessageSquare className='h-3.5 w-3.5' />
                        Notes ({openCommentThreadCount})
                      </TabsTrigger>
                    )}
                    {hasAssumptionsSidebar && (
                      <TabsTrigger
                        value='assumptions'
                        className={sidebarTabTriggerCls}
                      >
                        <Sparkles className='h-3.5 w-3.5' />
                        Assumptions ({pendingAssumptions.length})
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>

              <TabsContent
                value='properties'
                className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
              >
                <PolicyPropertiesPanel
                  policyData={_policyData ?? undefined}
                  programId={programId}
                  readOnly={isReadonlyMode}
                  mappedControls={item.mappedControls ?? []}
                />
              </TabsContent>

              {!isReadonlyMode && (
              <TabsContent
                value='health'
                className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
              >
                <PolicyHealthPanel
                  workspaceId={workspaceId}
                  programId={programId}
                  policySlug={item.slug}
                  policyDocumentId={effectivePolicyDocId ?? undefined}
                />
              </TabsContent>

              )}

              {hasAssumptionsSidebar && (
                <TabsContent
                  value='assumptions'
                  className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
                >
                  <ScrollArea
                    className='h-full'
                    viewClassName='[&>div]:!block [&>div]:space-y-2 p-3'
                  >
                    {assumptions.length === 0 ? (
                      <div className='text-text-subtle px-1 py-4 text-[12px]'>
                        No open assumptions. When the background drafter fills a
                        gap with a recommended default, it appears here for you
                        to confirm.
                      </div>
                    ) : (
                      <>
                        <div className='text-text-subtle px-1 pb-2 text-[11px] leading-[1.45]'>
                          During background drafting, these gaps were filled
                          with the recommended option (we don't ask during
                          onboarding). Confirm to lock the answer into the
                          policy context, or dismiss to move on.
                        </div>
                        {assumptions.map((a: any) => (
                          <AssumptionCard
                            key={a.id}
                            assumption={a}
                            isSelected={selectedAssumptionId === a.id}
                            isResolving={resolvingAssumptionId === a.id}
                            onClick={() => handleAssumptionClick(a)}
                            onConfirm={() =>
                              handleAssumptionDecision(a.id, 'confirmed')
                            }
                            onDismiss={() =>
                              handleAssumptionDecision(a.id, 'dismissed')
                            }
                          />
                        ))}
                      </>
                    )}
                  </ScrollArea>
                </TabsContent>
              )}

              <TabsContent
                value='claims'
                className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
              >
                <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                  <PolicyCommitmentsPanel
                    workspaceId={workspaceId}
                    auditCycleId={auditCycleId}
                    programId={programId}
                    policySlug={item.slug}
                    policyDocumentId={effectivePolicyDocId ?? undefined}
                    version={viewingVersion ?? undefined}
                    headerAction={highlightsToggle}
                    canManageTestLinks={!isReadonlyMode && viewingVersion === null && canWorkspaceWrite}
                    missingCoverageOnly={missingCoverageOnly}
                    onClearMissingCoverageFilter={() =>
                      setMissingCoverageOnly(false)
                    }
                    // Answering the questionnaire is not editing the document —
                    // it states what you actually do, and the projection rewrites
                    // the wording for you. Gating it behind the editor's draft
                    // mode made a read-only wall out of the panel's whole point.
                    // Approved policies still hold: changing a commitment there
                    // has to go through the approval flow.
                    editable={
                      !isReadonlyMode &&
                      viewingVersion === null &&
                      item.generationState !== 'approved'
                    }
                    selectedClaimId={selectedClaimId}
                    onSelectClaim={handleCommitmentClaimSelect}
                    onPolicyQueued={() => {
                      queryClient.invalidateQueries({
                        queryKey:
                          trpc.complianceProgram.getPolicyDocContent.queryKey({
                            policyDocumentId: effectivePolicyDocId ?? '',
                        }),
                      })
                    }}
                  />
                </div>
              </TabsContent>

              {hasCommentsRail && (
                <TabsContent
                  value='comments'
                  className='mt-0 flex min-h-0 flex-1 flex-col overflow-hidden'
                >
                  <div className='shrink-0 p-4 pb-2'>
                    <p className='text-text-placeholder text-[10px] leading-5 font-semibold tracking-wider uppercase'>
                      Select text in the document to add an inline review note,
                      or add a general review note here.
                    </p>
                  </div>
                  <div className='flex-1 overflow-hidden'>
                    <div className='h-full'>
                      <CommentPanel
                        docId={effectivePolicyDocId ?? ''}
                        comments={commentsController.comments}
                        onResolve={commentsController.resolveComment}
                        onDelete={commentsController.deleteComment}
                        onReply={commentsController.replyComment}
                        onAddPageComment={commentsController.addPageComment}
                        onCommentClick={handleCommentClick}
                        hideComposer={hideCommentComposer}
                        className='h-full'
                        threadsClassName='px-4'
                        composerClassName='px-4'
                        activeCommentId={activeCommentId}
                        activeChangeSource={activeCommentSource}
                      />
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      {claimToDelete && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'
          onClick={() => setClaimToDelete(null)}
        >
          <div
            className='w-full max-w-sm rounded-md bg-card p-6 shadow-xl'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='text-text-secondary text-sm font-semibold'>
              Delete claim?
            </h3>
            <p className='text-text-subtle mt-1 text-xs'>
              This action cannot be undone.
            </p>
            <div className='mt-4 flex justify-end gap-2'>
              <button
                onClick={() => setClaimToDelete(null)}
                className='border-border text-text-body hover:bg-muted rounded-sm border bg-card px-3 py-1.5 text-xs font-medium'
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  void handleConfirmClaimDelete()
                }}
                disabled={
                  deleteDraftClaimMutation.isPending ||
                  isDeleteConfirmedClaimPending
                }
                className='rounded-sm bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50'
              >
                {deleteDraftClaimMutation.isPending ||
                isDeleteConfirmedClaimPending
                  ? 'Deleting...'
                  : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bring-your-own-document path for an empty policy. Scoped to this
          slug so the panel offers one slot, not the whole open library. */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className='gap-1 overflow-hidden sm:max-w-[680px]'>
          <DialogHeader>
            <DialogTitle className='font-heading text-[16px] font-semibold'>
              Upload {item.title}
            </DialogTitle>
            <DialogDescription className='text-[12.5px]'>
              We read the document, map it to this policy and keep it as the
              approved content.
            </DialogDescription>
          </DialogHeader>
          <div className='min-w-0'>
            {programId ? (
              <PolicyUploadPanel
                programId={programId}
                mode='library'
                restrictToSlug={item.slug}
                onComplete={() => setUploadOpen(false)}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate path: answer this policy's own questions first, then the
          v2 generator composes the draft from the template. */}
      {programId && (
        <PolicyQuestionnaireDialog
          programId={programId}
          policySlug={item.slug}
          policyTitle={item.title}
          open={questionnaireOpen}
          onOpenChange={setQuestionnaireOpen}
          onRegenerate={() => {
            setQuestionnaireOpen(false)
            openPolicyChat()
          }}
        />
      )}
    </div>
  )
}
